import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '@app/components/shared/confirmation-dialog/confirmation-dialog.component';
import { IDialogResult } from '@app/components/shared/confirmation-dialog/model/dialog-models';
import { finalize, map, Observable, of, ReplaySubject, switchMap, take, tap } from 'rxjs';
import { PasswordService } from '@app/services/password/password.service';
import { defined } from '@app/fremework/functions';
import { UserToken } from '@app/services/password/model/user-token';

@Injectable({
  providedIn: 'root'
})
export class LoginService {
  private readonly loginDialogId = 'login-dialog';
  private pendingLogin?: ReplaySubject<UserToken>;

  constructor(
    private confirmationDialog: MatDialog,
    private passwordService: PasswordService) {
  }

  hasToken(raceGuid: string): boolean {
    return defined(this.passwordService.getToken(raceGuid));
  }

  checkLogin(raceGuid: string): Observable<UserToken> {

    const userToken = this.passwordService.getUserToken(raceGuid);
    if (userToken) {
      console.log('checkLogin: existing user token found', raceGuid);
      return of(userToken);
    }

    if (this.pendingLogin) {
      console.log('checkLogin: login already in progress', raceGuid);
      return this.pendingLogin.asObservable();
    }

    console.log('checkLogin: opening login dialog', raceGuid);
    const result$ = new ReplaySubject<UserToken>(1);
    this.pendingLogin = result$;

    // Subscribed eagerly so the login request is made even if the caller (e.g. a router guard) unsubscribes.
    this.openLogin()
      .afterClosed()
      .pipe(
        take(1),
        switchMap(dialogResult => {
          console.log('checkLogin: login dialog closed', { button: dialogResult?.button, hasInput: !!dialogResult?.inputMap });
          if (!dialogResult?.inputMap) {
            return of(undefined);
          }
          return this.passwordService
            .requestToken(
              dialogResult.inputMap.get('user')?.toLowerCase().trim(),
              dialogResult.inputMap.get('pwd')?.toLowerCase().trim(),
              dialogResult.inputMap.get('sign')?.trim(),
              raceGuid)
            .pipe(
              take(1),
              map(token => {
                this.passwordService.setToken(token);
                return defined(token?.token) ? token : undefined;
              }),
              tap({
                error: error => console.error('checkLogin: token request failed', {
                  status: error?.status,
                  name: error?.name
                })
              }));
        }),
        finalize(() => {
          if (this.pendingLogin === result$) {
            this.pendingLogin = undefined;
          }
        }))
      .subscribe(result$);

    return result$.asObservable();
  }

  openLogin = (): MatDialogRef<ConfirmationDialogComponent<IDialogResult>, IDialogResult> => {
    const existingDialog = this.confirmationDialog.getDialogById(this.loginDialogId);
    if (existingDialog) {
      return existingDialog as MatDialogRef<
        ConfirmationDialogComponent<IDialogResult>,
        IDialogResult
      >;
    }

    return this.confirmationDialog.open(ConfirmationDialogComponent, {
      id: this.loginDialogId,
      data: {
        title: 'Ange tävlingslösenord',
        message: undefined,
        bulletList: undefined,
        primaryButton: 'OK',
        secondaryButton: 'Avbryt',
        correlationId: 'login',
        inputs: [
          { label: 'Användarnamn', hint: 'användarnamn', keyValuepair: { key: 'user', value: '' }, autocomplete: 'username', name: 'username', inputType: 'text' },
          { label: 'Lösenord', hint: 'lösenord', keyValuepair: { key: 'pwd', value: '' }, autocomplete: 'current-password', name: 'password', inputType: 'password' },
          { label: 'Signatur namn', hint: 'signatur', keyValuepair: { key: 'sign', value: '' }, autocomplete: 'name', name: 'name', inputType: 'text' }]
      }
    });
  };

}
